import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, FileText, Tag, Clock, PhoneCall } from 'lucide-react';
import { RequestCallbackDialog } from '@/components/about/RequestCallbackDialog';
import logoFull from '@/assets/logo-full.svg';
import { AppFooter } from '@/components/layout/AppFooter';
import { getAboutContent } from '@/content/about';

const VALUE_ICONS = [FileText, Tag, Clock];

const About = () => {
  const { i18n } = useTranslation();
  const content = getAboutContent(i18n.language);
  const [callbackOpen, setCallbackOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      <div className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#37699E] to-[#0880AE] py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={16} />
              {content.back}
            </Link>

            <img src={logoFull} alt="Velox" className="h-8 mb-8 brightness-0 invert" loading="lazy" />

            <p className="text-xs font-medium uppercase tracking-wide text-[#FFBF07] mb-3">
              {content.eyebrowVision}
            </p>
            <h1 className="font-heading text-3xl md:text-5xl font-semibold text-white leading-tight max-w-3xl">
              {content.vision}
            </h1>
            <div className="w-24 h-1 bg-[#FFBF07] rounded-full my-6" />
            <p className="text-lg text-white/90">{content.tagline}</p>
          </div>
        </section>

        {/* About */}
        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <p className="text-xs font-medium uppercase tracking-wide text-[#0880AE] mb-3">
              {content.eyebrowAbout}
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#37699E] mb-4">
              {content.aboutTitle}
            </h2>
            <p className="text-base text-[#2D3748] whitespace-pre-line leading-relaxed">{content.aboutBody}</p>
          </div>
        </section>

        {/* Mission */}
        <section className="pb-12 md:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="bg-[#EBF4F8] rounded-2xl p-6 md:p-8 border-l-4 border-[#FFBF07]">
              <p className="text-xs font-medium uppercase tracking-wide text-[#FFBF07] mb-3">
                {content.eyebrowMission}
              </p>
              <p className="font-heading text-xl md:text-2xl font-medium text-[#37699E] leading-snug">
                {content.mission}
              </p>
            </div>
          </div>
        </section>

        {/* What we do differently */}
        <section className="pb-12 md:pb-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <p className="text-xs font-medium uppercase tracking-wide text-[#0880AE] mb-3">
              {content.eyebrowDifference}
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#37699E] mb-8 max-w-3xl">
              {content.differenceTitle}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {content.values.map((value, index) => {
                const Icon = VALUE_ICONS[index] ?? FileText;
                return (
                  <article
                    key={value.title}
                    className="bg-white rounded-xl border border-[#DBE2EA] p-6 shadow-sm hover:shadow-md hover:border-[#0880AE] transition-all duration-300"
                  >
                    <Icon className="text-[#0880AE] mb-4" size={24} />
                    <h3 className="font-heading text-xl font-medium text-[#37699E] mb-3">{value.title}</h3>
                    <p className="text-sm text-[#2D3748] leading-relaxed">{value.body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Founders */}
        <section className="pb-12 md:pb-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <p className="text-xs font-medium uppercase tracking-wide text-[#0880AE] mb-3">
              {content.eyebrowTeam}
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#37699E] mb-8 max-w-3xl">
              {content.teamTitle}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {content.founders.map((founder) => (
                <article key={founder.name} className="bg-white rounded-xl border border-[#DBE2EA] p-6">
                  <div className="w-16 h-16 rounded-full bg-[#87ADD4] flex items-center justify-center mb-4">
                    <span className="text-lg font-semibold text-white">
                      {founder.name
                        .replace(/[–-]/g, ' ')
                        .split(' ')
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((part) => part[0])
                        .join('')}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-medium text-[#37699E]">{founder.name}</h3>
                  <p className="text-xs font-medium uppercase tracking-wide text-[#5A6B7A] mt-1 mb-3">
                    {founder.role}
                  </p>
                  <p className="text-sm text-[#2D3748] leading-relaxed">{founder.bio}</p>
                </article>
              ))}
            </div>
            <p className="text-sm text-[#5A6B7A] leading-relaxed mt-6 max-w-3xl">{content.teamNote}</p>
          </div>
        </section>

        {/* Luxembourg + future */}
        <section className="pb-12 md:pb-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-8">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-[#0880AE] mb-3">
                {content.eyebrowLuxembourg}
              </p>
              <h2 className="font-heading text-2xl font-medium text-[#37699E] mb-4">{content.luxembourgTitle}</h2>
              <p className="text-base text-[#2D3748] whitespace-pre-line leading-relaxed">
                {content.luxembourgBody}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-[#DBE2EA] p-6">
              <h2 className="font-heading text-2xl font-medium text-[#37699E] mb-4">{content.futureTitle}</h2>

              <p className="text-base text-[#2D3748] leading-relaxed">{content.futureBody}</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="bg-[#EBF4F8] rounded-2xl p-6 md:p-8 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#37699E] mb-4">
                {content.ctaTitle}
              </h2>
              <p className="text-base text-[#2D3748] max-w-2xl mx-auto mb-6">{content.ctaBody}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/auth?mode=signup"
                  className="inline-flex items-center gap-2 bg-[#FFBF07] hover:bg-[#E5AB06] active:bg-[#CC9805] text-[#2D3748] font-semibold px-6 py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                >
                  {content.ctaPrimary}
                  <ArrowRight size={18} />
                </Link>
                <button
                  type="button"
                  onClick={() => setCallbackOpen(true)}
                  className="inline-flex items-center gap-2 bg-white border-2 border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
                >
                  {content.ctaSecondary}
                  <PhoneCall size={18} />
                </button>
              </div>
              <a
                href="mailto:info@myvelox.eu"
                className="inline-block text-sm text-[#0880AE] hover:text-[#076A8E] transition-colors mt-6"
              >
                info@myvelox.eu
              </a>
            </div>
          </div>
        </section>
      </div>
      <AppFooter variant="app" />
      <RequestCallbackDialog
        open={callbackOpen}
        onOpenChange={setCallbackOpen}
        copy={content.callback}
        language={i18n.language}
      />
    </div>
  );
};

export default About;
